/**
 * Learner actions — state updates, answer recording, XP, streaks, mastery.
 *
 * Extracted from store.ts as part of T1 (store.ts opsplitsen).
 */

import type { SetFn, GetFn, AppStoreState } from './types'
import { QUESTION_BY_ID, getFamilyDefinition, TAXONOMY } from '../../content'
import {
  LEVEL_THRESHOLDS,
  XP_REWARDS,
  getLevelForXP,
  calculateXP,
} from '../../config/constants'
import { getActiveAccount } from '../../accounts'
import {
  updateQuestionState,
  getQuestionStateKey,
  updateFamilyMastery,
  getFamilyMasteryKey,
  buildPlacementInferredEntry,
} from '../../mastery'
import { warn } from '../../utils/logger'
import { deriveSelectedGroup } from './helpers'

type LearnerActions = Pick<
  AppStoreState,
  | 'updateLearnerState'
  | 'markCheatSheetViewed'
  | 'recordAnswer'
  | 'seedInferredMastery'
  | 'clearPendingLevelUp'
  | 'devJumpToLevel'
  | 'markAchievementUnlocked'
  | 'updateDailyChallengeProgress'
>

export function createLearnerActions(set: SetFn, get: GetFn): LearnerActions {
  return {
    updateLearnerState: (updater) => {
      set((s) => {
        if (!s.multiAccountState) return s
        const active = getActiveAccount(s.multiAccountState.accounts)
        if (!active || active.role !== 'learner') {
          warn('Cannot update state for non-learner account')
          return s
        }
        return {
          multiAccountState: {
            ...s.multiAccountState,
            learnerData: {
              ...s.multiAccountState.learnerData,
              [active.id]: updater(s.multiAccountState.learnerData[active.id]),
            },
          },
        }
      })
    },

    markCheatSheetViewed: (moduleId) => {
      get().updateLearnerState((state) => {
        const current = state.progress[moduleId] || { cheatSheetViewed: false }
        return {
          ...state,
          progress: {
            ...state.progress,
            [moduleId]: { ...current, cheatSheetViewed: true },
          },
        }
      })
    },

    markAchievementUnlocked: (achievementId) => {
      get().updateLearnerState((state) => {
        const currentUnlocked = state.stats.unlockedAchievements || []
        if (currentUnlocked.includes(achievementId)) return state

        return {
          ...state,
          stats: {
            ...state.stats,
            unlockedAchievements: [...currentUnlocked, achievementId],
          },
        }
      })
    },

    recordAnswer: (
      moduleId,
      questionId,
      isCorrect,
      userChoice,
      payload,
      errorType,
      delta,
      timeMs,
      helpUsed,
      skipped,
    ) => {
      // Use derived group as single source of truth — eliminates the prior
      // questionGroup vs selectedGroup mismatch where questionStates and
      // familyMastery could be keyed under different groups for the same answer.
      const selectedGroup = deriveSelectedGroup(get())
      get().updateLearnerState((prev) => {
        const question = QUESTION_BY_ID.get(questionId)

        // Both questionState and familyMastery use the learner's current group.
        const stateKey = getQuestionStateKey(questionId, selectedGroup)
        const currentQuestionState = prev.questionStates[stateKey]
        const newQuestionState = updateQuestionState(
          currentQuestionState,
          questionId,
          isCorrect,
          timeMs,
        )
        const newQuestionStates = {
          ...prev.questionStates,
          [stateKey]: newQuestionState,
        }

        // Update familyId mastery tracking (CANONICAL MASTERY SOURCE - GROUP-SCOPED)
        let newFamilyMastery = prev.familyMastery ?? {}
        if (question?.familyId) {
          const familyId = question.familyId
          const questionTier =
            question.tier ?? getFamilyDefinition(familyId)?.tier ?? 1
          const masteryKey = getFamilyMasteryKey(familyId, selectedGroup)
          const currentFamilyState = newFamilyMastery[masteryKey]
          const updatedFamilyState = updateFamilyMastery(
            currentFamilyState,
            familyId,
            isCorrect,
            questionId,
            questionTier,
            payload,
            errorType,
            delta,
            helpUsed,
            skipped,
          )
          newFamilyMastery = {
            ...newFamilyMastery,
            [masteryKey]: updatedFamilyState,
          }
        }

        // Streak tracking — skip is treated as "not correct", so the streak
        // breaks. This matches the design intent: skipping is honest but
        // still means the learner didn't complete the question.
        const currentStreakState = prev.streak || {
          currentTotalStreak: 0,
          longestTotalStreak: 0,
        }

        let newCurrentStreak = currentStreakState.currentTotalStreak
        if (isCorrect) {
          newCurrentStreak += 1
        } else {
          newCurrentStreak = 0
        }

        const newLongestStreak = Math.max(
          currentStreakState.longestTotalStreak,
          newCurrentStreak,
        )

        const newStreakState = {
          currentTotalStreak: newCurrentStreak,
          longestTotalStreak: newLongestStreak,
          lastStreakQuestionId: questionId,
        }

        // Daily goal tracking — skipped questions do NOT count toward the
        // daily goal. The child shouldn't be able to hit their goal by
        // tapping "ik weet het niet" repeatedly.
        const currentDailyStreak = prev.dailyStreak || {
          dailyGoalTarget: 10,
          todayQuestionsAnswered: 0,
          todayGoalReached: false,
          weeklyGoalTarget: 3,
          weeklyGoalReached: false,
          currentWeekDays: 0,
          consecutiveFullWeeks: 0,
          longestWeekStreak: 0,
          lastActiveDate: new Date().toISOString().split('T')[0],
          weeklyHistory: {},
        }

        // Simple daily increment (midnight reset is handled in useAppStore).
        // Skipped questions do not increment — they do not count as a
        // completed answer for daily-goal purposes.
        const newTodayQuestions = skipped
          ? currentDailyStreak.todayQuestionsAnswered
          : currentDailyStreak.todayQuestionsAnswered + 1
        const newlyReachedGoal =
          !skipped &&
          !currentDailyStreak.todayGoalReached &&
          newTodayQuestions >= currentDailyStreak.dailyGoalTarget

        let newCurrentWeekDays = currentDailyStreak.currentWeekDays
        let newlyReachedWeeklyGoal = false

        if (newlyReachedGoal) {
          newCurrentWeekDays += 1
          if (
            !currentDailyStreak.weeklyGoalReached &&
            newCurrentWeekDays >= currentDailyStreak.weeklyGoalTarget
          ) {
            newlyReachedWeeklyGoal = true
          }
        }

        const newDailyStreak = {
          ...currentDailyStreak,
          todayQuestionsAnswered: newTodayQuestions,
          todayGoalReached:
            currentDailyStreak.todayGoalReached || newlyReachedGoal,
          currentWeekDays: newCurrentWeekDays,
          weeklyGoalReached:
            currentDailyStreak.weeklyGoalReached || newlyReachedWeeklyGoal,
          // HC-7/HC-8 fix: track lifetime daily goals reached
          totalDailyGoalsReached:
            (currentDailyStreak.totalDailyGoalsReached ?? 0) +
            (newlyReachedGoal ? 1 : 0),
        }

        // XP calculation — skipped questions earn 0 XP. Honest skip is
        // free of punishment but also free of reward.
        let xpGain = skipped ? 0 : calculateXP(isCorrect, question?.tier)

        // Apply bonuses (impossible if skipped — newlyReachedGoal is gated)
        if (newlyReachedGoal) {
          xpGain += XP_REWARDS.DAILY_GOAL
        }
        if (newlyReachedWeeklyGoal) {
          xpGain +=
            currentDailyStreak.weeklyGoalTarget >= 5
              ? XP_REWARDS.WEEK_5_DAYS
              : XP_REWARDS.WEEK_3_DAYS
        }

        const newXP = prev.stats.xp + xpGain
        const oldLevel = prev.stats.level
        const newLevel = getLevelForXP(newXP)
        const pendingLevelUp =
          newLevel > oldLevel ? newLevel : prev.pendingLevelUp

        return {
          ...prev,
          stats: { ...prev.stats, xp: newXP, level: newLevel },
          progress: prev.progress,
          questionStates: newQuestionStates,
          familyMastery: newFamilyMastery,
          streak: newStreakState,
          dailyStreak: newDailyStreak,
          pendingLevelUp,
        }
      })
    },

    clearPendingLevelUp: () => {
      get().updateLearnerState((prev) => ({
        ...prev,
        pendingLevelUp: undefined,
      }))
    },

    seedInferredMastery: (group, placedTier) => {
      // FG3 (S6) — Walk the taxonomy once, compute an inferred entry for
      // every family whose tier ≤ placedTier, and merge into the store.
      // We DO NOT overwrite an existing entry for the same (familyId, group)
      // — the existing one is either still valid (same placement run) or
      // was already supplanted by an observed signal upstream. This makes
      // the action idempotent and safe to call from any onboarding hook.
      get().updateLearnerState((prev) => {
        const existing = prev.inferredMastery ?? {}
        const next = { ...existing }
        const now = Date.now()
        let seeded = 0
        for (const family of TAXONOMY) {
          const key = getFamilyMasteryKey(family.familyId, group)
          if (next[key]) continue // preserve existing soft signal
          const entry = buildPlacementInferredEntry({
            familyId: family.familyId,
            group,
            familyTier: family.tier,
            placedTier,
            now,
          })
          if (!entry) continue // family above placed tier — no signal
          next[key] = entry
          seeded++
        }
        if (seeded === 0) return prev
        return { ...prev, inferredMastery: next }
      })
    },

    devJumpToLevel: (targetLevel) => {
      get().updateLearnerState((prev) => ({
        ...prev,
        stats: {
          ...prev.stats,
          xp: LEVEL_THRESHOLDS[targetLevel - 1] || 0,
          level: targetLevel,
        },
      }))
    },

    // savePlacementResult removed — placement test was removed and the action
    // wrote to a dead-store. See PR_SELECTEDGROUP_CLEANUP.md.

    updateDailyChallengeProgress: (amount, challengeType, targetCount) => {
      get().updateLearnerState((state) => {
        const today = new Date().toISOString().split('T')[0]
        const currentChallenge = state.dailyChallenge

        // Reset if it's a new day
        const isNewDay = currentChallenge?.lastChallengeDate !== today
        const currentProgress = isNewDay ? 0 : currentChallenge?.progress || 0
        const wasCompleted = isNewDay
          ? false
          : currentChallenge?.completedToday || false

        if (wasCompleted) return state // Already done today

        const newProgress = currentProgress + amount
        const isCompletedNow = newProgress >= targetCount

        let newStats = state.stats
        let newPendingLevelUp = state.pendingLevelUp

        // Award 50 XP if just completed
        if (isCompletedNow) {
          const rewardXP = 50
          const newXP = state.stats.xp + rewardXP
          const newLevel = getLevelForXP(newXP)
          const leveledUp = newLevel > state.stats.level

          newStats = {
            ...state.stats,
            xp: newXP,
            level: newLevel,
          }

          if (leveledUp) {
            newPendingLevelUp = newLevel
          }
        }

        return {
          ...state,
          stats: newStats,
          pendingLevelUp: newPendingLevelUp,
          dailyChallenge: {
            lastChallengeDate: today,
            challengeType,
            progress: newProgress,
            completedToday: isCompletedNow,
          },
        }
      })
    },
  }
}
